import { useEffect, useState } from 'react';
import Modal from './ui/Modal.jsx';
import { pb } from '../lib/pocketbase.js';
import { heuteAlsInputValue, toDateInputValue } from '../lib/aktivitaet.js';

const EMPTY = {
  titel: '',
  version: '',
  veroeffentlicht_am: '',
  aktiv: true,
  inhalt: '',
};

/**
 * Anlege-/Bearbeiten-Modal für Update-Einträge.
 * Inhalt wird als HTML eingetragen — der PB-Editor speichert ohnehin HTML,
 * und ein voller Richtext-Editor lohnt sich für interne Changelog-Pflege
 * nicht. Hinweistext im Modal erklärt, welche Tags üblich sind.
 */
export default function UpdateFormModal({ open, onClose, update, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (update) {
      setForm({
        titel: update.titel ?? '',
        version: update.version ?? '',
        veroeffentlicht_am: toDateInputValue(update.veroeffentlicht_am),
        aktiv: update.aktiv ?? true,
        inhalt: update.inhalt ?? '',
      });
    } else {
      setForm({ ...EMPTY, veroeffentlicht_am: heuteAlsInputValue() });
    }
    setError(null);
  }, [open, update]);

  function handleChange(field) {
    return (e) => {
      const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!form.titel.trim()) {
      setError('Bitte einen Titel angeben.');
      return;
    }
    if (!form.inhalt.trim()) {
      setError('Bitte einen Inhalt angeben.');
      return;
    }
    if (!form.veroeffentlicht_am) {
      setError('Bitte ein Veröffentlichungsdatum angeben.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        titel: form.titel.trim(),
        version: form.version.trim(),
        veroeffentlicht_am: form.veroeffentlicht_am,
        aktiv: !!form.aktiv,
        inhalt: form.inhalt,
      };
      let saved;
      if (update?.id) {
        saved = await pb.collection('updates').update(update.id, payload);
      } else {
        saved = await pb.collection('updates').create(payload);
      }
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(err?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={update?.id ? 'Update bearbeiten' : 'Neues Update anlegen'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label">Titel *</label>
            <input
              type="text"
              required
              className="input mt-1"
              value={form.titel}
              onChange={handleChange('titel')}
            />
          </div>

          <div>
            <label className="label">Version</label>
            <input
              type="text"
              className="input mt-1"
              placeholder="z. B. 1.3.0"
              value={form.version}
              onChange={handleChange('version')}
            />
          </div>

          <div>
            <label className="label">Veröffentlicht am *</label>
            <input
              type="date"
              required
              className="input mt-1"
              value={form.veroeffentlicht_am}
              onChange={handleChange('veroeffentlicht_am')}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                checked={!!form.aktiv}
                onChange={handleChange('aktiv')}
              />
              Aktiv (im Login-Gate und in der Neuigkeiten-Liste anzeigen)
            </label>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Inhalt (HTML) *</label>
            <textarea
              rows={14}
              required
              className="input mt-1 font-mono text-xs"
              value={form.inhalt}
              onChange={handleChange('inhalt')}
              placeholder="<h2>Überschrift</h2>&#10;<p>Beschreibung mit <strong>fett</strong> oder <em>kursiv</em></p>&#10;<ul><li>Bullet 1</li></ul>"
            />
            <p className="mt-1 text-xs text-slate-500">
              Erlaubt sind klassische HTML-Tags wie <code>&lt;h2&gt;</code>,{' '}
              <code>&lt;h3&gt;</code>, <code>&lt;p&gt;</code>,{' '}
              <code>&lt;strong&gt;</code>, <code>&lt;ul&gt;</code>,{' '}
              <code>&lt;li&gt;</code>.
            </p>
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}

        <div className="sticky bottom-0 -mx-6 -mb-5 mt-6 flex justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Speichern …' : 'Speichern'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
